import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/shared/Layout/Layout';
import Button from '../../../components/shared/Button/Button';
import {
  SearchContainer,
  SearchHeader,
  SearchBar,
  SearchInput,
  FiltersSection,
  FilterGroup,
  FilterLabel,
  FilterSelect,
  ResultsSection,
  ResultsCount,
  ActivityGrid,
  ActivityCard,
  ActivityImageWrap,
  ActivityImage,
  ActivityBadge,
  ActivityInfo,
  ActivityTitle,
  ActivityDescription,
  ActivityMeta,
  ActivityPrice,
  ActivityLocation,
  NoResults,
} from './Search.styles';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400&h=300&fit=crop';

const Search = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [activityTypes, setActivityTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({
    location: '',
    priceRange: '',
    duration: '',
    type: '',
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivityTypes();
    loadLocations();
  }, []);

  useEffect(() => {
    // Pequeño debounce para no disparar una búsqueda por tecla
    const timer = setTimeout(() => {
      performSearch();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filters]);

  const performSearch = async () => {
    setLoading(true);

    try {
      const queryParams = new URLSearchParams({
        ...(searchTerm && { search: searchTerm }),
        ...(filters.location && { location: filters.location }),
        ...(filters.type && { type: filters.type }),
        ...(filters.priceRange && { priceRange: filters.priceRange }),
        ...(filters.duration && { duration: filters.duration }),
        page: 1,
        limit: 12,
      });

      const response = await fetch(`/api/activities?${queryParams}`);
      const data = await response.json();
      if (data.success) {
        setResults(data.activities);
      }
    } catch (error) {
      console.error('Error searching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivityTypes = async () => {
    try {
      const response = await fetch('/api/activities/types');
      const data = await response.json();
      if (data.success) {
        setActivityTypes(data.types);
      }
    } catch (error) {
      console.error('Error loading activity types:', error);
    }
  };

  const loadLocations = async () => {
    try {
      const response = await fetch('/api/activities/locations');
      const data = await response.json();
      if (data.success) {
        setLocations(data.locations);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  return (
    <Layout>
      <SearchContainer>
        <SearchHeader>
          <span className="eyebrow">Explora</span>
          <h1>Buscar actividades</h1>
          <p>Encuentra la experiencia perfecta para tu próximo viaje</p>
        </SearchHeader>

        <SearchBar>
          <SearchInput
            type="text"
            placeholder="Buscar actividades, tours, atracciones..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button onClick={performSearch}>Buscar</Button>
        </SearchBar>

        <FiltersSection>
          <FilterGroup>
            <FilterLabel>Ubicación</FilterLabel>
            <FilterSelect
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
            >
              <option value="">Todas las ubicaciones</option>
              {locations.map(location => (
                <option key={location.key} value={location.label}>
                  {location.label} ({location.count})
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Tipo</FilterLabel>
            <FilterSelect
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <option value="">Todos los tipos</option>
              {activityTypes.map(type => (
                <option key={type.key} value={type.label}>
                  {type.label} ({type.count})
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Duración</FilterLabel>
            <FilterSelect
              value={filters.duration}
              onChange={(e) => handleFilterChange('duration', e.target.value)}
            >
              <option value="">Cualquier duración</option>
              <option value="1-3">1-3 horas</option>
              <option value="4-6">4-6 horas</option>
              <option value="7+">7+ horas</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Precio</FilterLabel>
            <FilterSelect
              value={filters.priceRange}
              onChange={(e) => handleFilterChange('priceRange', e.target.value)}
            >
              <option value="">Cualquier precio</option>
              <option value="low">$0 - $500</option>
              <option value="medium">$501 - $1,500</option>
              <option value="high">$1,501+</option>
            </FilterSelect>
          </FilterGroup>
        </FiltersSection>

        <ResultsSection>
          <ResultsCount>
            {loading ? 'Buscando...' : `${results.length} actividades encontradas`}
          </ResultsCount>

          {!loading && results.length === 0 ? (
            <NoResults>
              <h3>No se encontraron resultados</h3>
              <p>Intenta con otros términos de búsqueda o ajusta los filtros</p>
            </NoResults>
          ) : (
            <ActivityGrid>
              {results.map(activity => (
                <ActivityCard
                  key={activity.id}
                  onClick={() => navigate(`/activity/${activity.id}`)}
                >
                  <ActivityImageWrap>
                    <ActivityImage
                      src={activity.images?.[0] || FALLBACK_IMAGE}
                      alt={activity.title}
                      loading="lazy"
                    />
                    {activity.type && <ActivityBadge>{activity.type}</ActivityBadge>}
                  </ActivityImageWrap>
                  <ActivityInfo>
                    <ActivityTitle>{activity.title}</ActivityTitle>
                    <ActivityDescription>{activity.description}</ActivityDescription>
                    <ActivityLocation>📍 {activity.location}</ActivityLocation>
                    <ActivityMeta>
                      <ActivityPrice>
                        ${activity.price?.toLocaleString() || 0}
                      </ActivityPrice>
                      <span className="duration">{activity.duration}</span>
                    </ActivityMeta>
                  </ActivityInfo>
                </ActivityCard>
              ))}
            </ActivityGrid>
          )}
        </ResultsSection>
      </SearchContainer>
    </Layout>
  );
};

export default Search;
